// src/gen/generator.ts
// Reverse-construction generator + replay-to-verify gate (§3, §10, §11).
//
// Principle (§3.1): start from the SOLVED state (empty board + gates) and apply random REVERSE
// moves. Each reverse move is the exact inverse of a legal forward move, so the recorded forward
// sequence — taken in reverse application order — is a guaranteed forward solution. Every reverse
// move below is annotated with WHY its recorded forward move is its exact inverse (M1).
//
// Hay note (§14): the v1 core generator NEVER attaches `cover`. Covered blocks are not
// exit-eligible (§6.4), so adding cover here would break the verify gate. Hay is layered onto a
// finished, solvable level by obstacles.ts (Phase 4) without re-verification.

import type { GameState } from '../game/rules';
import type { Board, Gate } from '../game/board';
import type { Block } from '../game/block';
import type { BlockId, Cell, Color, Direction, Move, Side } from '../game/types';
import type { Rng } from './prng';
import type { ResolvedDifficulty } from './difficulty';

import { COLORS, DIRECTIONS, SIDE_EXIT_DIRECTION, opposite } from '../game/types';
import { createBoard, gateCoversLanes, isInside } from '../game/board';
import { blockCells, blockFromShapeId, blockLanes, translateBlock } from '../game/block';
import { SHAPES, shapeBounds, shapeLanes, shapeLeadEdge } from '../game/shapes';
import type { ShapeId } from '../game/shapes';
import {
  applyMove,
  blockAt,
  exitDistance,
  freeSlideDistance,
  getBlock,
  isResting,
  isSolved,
  resolveSlide,
} from '../game/rules';
import { mulberry32, nextInt, nextRange, pick, shuffle } from './prng';
import { resolveDifficulty, SHAPE_PACK_DENSITY } from './difficulty';

// ---- 10.1 Seed mapping (LOCKED) --------------------------------------------

/** ASCII "SLID" = 0x534C4944 (1397966148). Locked; changing it reshuffles every level. */
export const BASE_SEED: number = 0x534c4944;

/** seed = BASE_SEED + levelIndex (§3.3). levelIndex is 1-based. */
export function seedForLevel(levelIndex: number): number {
  return BASE_SEED + levelIndex;
}

// ---- 10.2 Public output & entry points -------------------------------------

export interface GeneratedLevel {
  readonly levelIndex: number; // 1-based; free-play uses -1
  readonly seed: number; // the seed that actually produced this level (post-retry)
  readonly difficulty: ResolvedDifficulty;
  readonly initialState: GameState; // puzzle START: board + scrambled blocks
  readonly solution: readonly Move[]; // forward solution; replaying on initialState clears the board
  readonly par: number; // === solution.length (§2.6, §3.3)
}

export interface GenerateOptions {
  /** Verify-gate retries (each bumps seed by +1). Omit ⇒ default 64. */
  readonly maxRetries?: number;
}

const DEFAULT_MAX_RETRIES = 64;

// ---- 10.3 Reverse-move model (exported for unit tests) — the EXACT duality --

export type ReverseMoveKind = 'spawn' | 'slide';

export interface ReverseMove {
  readonly kind: ReverseMoveKind;
  /**
   * The recorded FORWARD move that exactly inverts this reverse move:
   * 'spawn': forward.blockId = the newly spawned block; forward.dir = the gate's exit direction
   *          ⇒ applying it forward EXITS (removes) that block.
   * 'slide': forward.blockId = the existing block; forward.dir = +d (the direction it rests against)
   *          ⇒ applying it forward SLIDES it back exactly to its pre-reverse rest cell.
   */
  readonly forward: Move;
}

export interface ReverseStep {
  readonly state: GameState; // state AFTER applying the reverse move (new object; input not mutated)
  readonly reverse: ReverseMove;
}

/**
 * Anchor (bounding-box min corner) that places a `sb`-sized shape FLUSH inside `gate`, with its
 * lanes aligned to the gate lines. Mirrors the spawn formulas (§3.2.1, polyomino generalization):
 *   top    -> { row: 0,            col: gate.line }
 *   bottom -> { row: rows - sb.rows, col: gate.line }
 *   left   -> { row: gate.line,     col: 0 }
 *   right  -> { row: gate.line,     col: cols - sb.cols }
 * Because shapes are normalized (min row/col = 0) and the catalog's lanes are contiguous starting at
 * 0, blockLanes(block, exitDir) == gateLines(gate) exactly (span == shapeLanes(shape, side).length).
 */
function anchorForGate(rows: number, cols: number, gate: Gate, sb: { rows: number; cols: number }): Cell {
  switch (gate.side) {
    case 'top':
      return { row: 0, col: gate.line };
    case 'bottom':
      return { row: rows - sb.rows, col: gate.line };
    case 'left':
      return { row: gate.line, col: 0 };
    case 'right':
      return { row: gate.line, col: cols - sb.cols };
    default: {
      const _exhaustive: never = gate.side;
      return _exhaustive;
    }
  }
}

/**
 * REVERSE-EXIT / spawn (§3.2.1, polyomino): place a fresh `shapeId` block of `gate.color` FLUSH
 * inside `gate`, lanes aligned to the gate lines. Returns null iff any of its cells is off-board or
 * occupied, or — defensively — the flush placement does not resolve to a forward 'exited' (it always
 * does for a flat-edge shape/side, which is the only combination the planner ever assigns).
 */
export function reverseExit(
  state: GameState,
  gate: Gate,
  nextBlockId: BlockId,
  shapeId: ShapeId,
): ReverseStep | null {
  const board = state.board;
  const sb = shapeBounds(SHAPES[shapeId].cells);
  // The shape's perpendicular extent must fit the board (lane extent is guaranteed by the planner's
  // span/line choice, but a too-tall/wide shape for this side cannot be placed flush).
  if (sb.rows > board.rows || sb.cols > board.cols) {
    return null;
  }
  const anchor = anchorForGate(board.rows, board.cols, gate, sb);
  const block = blockFromShapeId(nextBlockId, gate.color, anchor, shapeId);
  const dir = SIDE_EXIT_DIRECTION[gate.side];
  for (const cell of blockCells(block)) {
    if (!isInside(board, cell) || blockAt(state, cell) !== undefined) {
      return null;
    }
  }
  // Lanes must coincide with the gate lines exactly (auto-fit invariant); else this is not an inverse.
  if (!gateCoversLanes(gate, blockLanes(block, dir))) {
    return null;
  }
  const blocks: readonly Block[] = [...state.blocks, block];
  const next: GameState = { board, blocks };
  // WHY EXACT INVERSE (M1, spawn, polyomino): the new block sits FLUSH against the gate's edge with
  // matching color and its lanes aligned to the gate lines. The planner only assigns a (shape, side)
  // whose leading silhouette toward `side` is FLAT, so a forward slide in SIDE_EXIT_DIRECTION carries
  // its ENTIRE leading edge off-board on the first step — a clean, neighbour-independent removal
  // (distance-0 'exited'). Applying that forward move therefore deletes exactly this block and lands
  // back on `state`. The defensive resolveSlide check below makes that contract explicit: if for any
  // reason the flush placement is not exit-eligible, we refuse the spawn so the caller retries.
  if (resolveSlide(next, nextBlockId, dir, exitDistance(board)).kind !== 'exited') {
    return null;
  }
  const reverse: ReverseMove = {
    kind: 'spawn',
    // The forward inverse is an EXIT: encode it at EXIT_DISTANCE so the slide carries the spawned
    // block off-board through its gate (flush + flat edge -> a distance-0 'exited').
    forward: { blockId: nextBlockId, dir, distance: exitDistance(board) },
  };
  return { state: next, reverse };
}

/**
 * REVERSE-SLIDE (§3.2.2): pick a uniformly-random legal reverse-slide and apply it. Legal candidate
 * = (block b, dir d) where forward {b,d} is a NO-OP (b rests against wall/block/non-matching gate in
 * +d) AND the opposite run (-d) has >= 1 empty interior cell. Slide b in -d by k ∈ [1,
 * freeSlideDistance(state,b,-d)]. Returns null iff no legal reverse-slide exists.
 */
export function reverseSlide(state: GameState, rng: Rng): ReverseStep | null {
  const candidates: { readonly block: Block; readonly d: Direction }[] = [];
  for (const block of state.blocks) {
    for (const d of DIRECTIONS) {
      // forward {block, d} must be a no-op: block is flush against wall/block/non-matching gate.
      if (!isResting(state, block.id, d)) {
        continue;
      }
      // and the opposite run must offer >= 1 empty interior cell to retract into.
      if (freeSlideDistance(state, block.id, opposite(d)) >= 1) {
        candidates.push({ block, d });
      }
    }
  }
  if (candidates.length === 0) {
    return null;
  }
  const chosen = pick(rng, candidates);
  const back = opposite(chosen.d);
  const maxK = freeSlideDistance(state, chosen.block.id, back); // >= 1 by candidate condition
  const k = nextRange(rng, 1, maxK);
  const moved = translateBlock(chosen.block, back, k);
  const blocks = state.blocks.map((b) => (b.id === chosen.block.id ? moved : b));
  const next: GameState = { board: state.board, blocks };
  // WHY EXACT INVERSE (M1, slide): we retracted `block` k cells in -d through cells that were empty
  // in `state`, and in `state` the block RESTED against an obstacle in +d (isResting was true).
  // Sliding it forward in +d retraces exactly those k now-empty cells and re-rests against the SAME
  // (unchanged) obstacle, returning it to its pre-retraction cell. It is a real 'slid' of distance
  // k and never an exit, because +d was a non-exit no-op in `state`.
  const reverse: ReverseMove = {
    kind: 'slide',
    // The forward inverse slides the block exactly k cells back in +d to re-rest against the same
    // obstacle: a real 'slid' of distance k (never an exit, since +d was a non-exit no-op).
    forward: { blockId: chosen.block.id, dir: chosen.d, distance: k },
  };
  return { state: next, reverse };
}

// ---- 10.4 gate auto-fit + per-color shape selection ------------------------

const SIDES: readonly Side[] = ['top', 'bottom', 'left', 'right'];

// SHAPE_PACK_DENSITY imported from difficulty.ts (authoritative single source).
// Full rationale: max packing density the constructive spawner can reliably reach; bounds the
// per-block shape AREA (maxArea = floor(SHAPE_PACK_DENSITY * boardArea / blockCount)) so that
// even if EVERY block is the largest allowed shape total footprint stays <= this board fraction —
// leaving room for reverse-slide retractions. Tuned against 2400-seed proof suite + levels 1..400.
// Raised to 0.90 in the pack-it-denser re-tune: even at the dense 0.30-area block cap the spawner
// stays reliably solvable (it packs ~96% of the board before failing), while the higher density lifts
// realized fill toward the busy ~67-69% (avg) the reference image shows. Note 0.90 with the 0.30 cap
// keeps the per-block budget at maxArea == 3 on the fullest 7x7 boards — the empirical fill optimum
// (a varied mix of short bars + L/T bends); raising either knob further shrinks pieces and lowers fill.

/** Inmost interior cell for a (side, line) against an rows×cols board (mirrors gateInnerCell). */
function innerCellOf(rows: number, cols: number, side: Side, line: number): Cell {
  switch (side) {
    case 'top':
      return { row: 0, col: line };
    case 'bottom':
      return { row: rows - 1, col: line };
    case 'left':
      return { row: line, col: 0 };
    case 'right':
      return { row: line, col: cols - 1 };
    default: {
      const _exhaustive: never = side;
      return _exhaustive;
    }
  }
}

function edgeLen(rows: number, cols: number, side: Side): number {
  return side === 'top' || side === 'bottom' ? cols : rows;
}

/**
 * True iff `shapeId`'s leading silhouette toward `side` is FLAT: every lane's forward-most cell sits
 * at the same depth, so a flush slide carries the ENTIRE leading edge off-board on one step. This is
 * the condition that makes a polyomino's gate exit clean and neighbour-INDEPENDENT — the key to the
 * spawn being an exact inverse of a forward exit (see reverseExit). S/Z tetrominoes have NO flat
 * side (their silhouette staircases on every side) and are therefore never assigned to a gate.
 */
function isFlatSide(shapeId: ShapeId, side: Side): boolean {
  const edge = shapeLeadEdge(SHAPES[shapeId].cells, side);
  if (edge.length === 0) {
    return false;
  }
  const vertical = side === 'top' || side === 'bottom';
  const depth = (c: Cell): number => (vertical ? c.row : c.col);
  const first = depth(edge[0]!);
  return edge.every((c) => depth(c) === first);
}

/** Sides on which `shapeId` both FITS the board and presents a flat leading edge. */
function placeableSides(rows: number, cols: number, shapeId: ShapeId): readonly Side[] {
  const sb = shapeBounds(SHAPES[shapeId].cells);
  if (sb.rows > rows || sb.cols > cols) {
    return [];
  }
  return SIDES.filter((s) => isFlatSide(shapeId, s));
}

/** Span (lane width) the shape occupies on `side` == the gate span auto-fit to it. */
function spanOf(shapeId: ShapeId, side: Side): number {
  return shapeLanes(SHAPES[shapeId].cells, side).length;
}

/**
 * Shapes eligible THIS level: in the difficulty pool, fitting the board, having a flat exit side, and
 * within the per-block area budget. Always non-empty (contains at least 'dot').
 */
function feasibleShapes(difficulty: ResolvedDifficulty): readonly ShapeId[] {
  const { rows, cols, blockCount, shapePool } = difficulty;
  const maxArea = Math.max(1, Math.floor((SHAPE_PACK_DENSITY * rows * cols) / blockCount));
  const out = shapePool.filter((id) => {
    const cells = SHAPES[id].cells;
    return cells.length <= maxArea && placeableSides(rows, cols, id).length > 0;
  });
  return out.length > 0 ? out : ['dot'];
}

/** All covered (segment, innerCell) keys for a gate placement. */
function gateKeys(
  rows: number,
  cols: number,
  side: Side,
  line: number,
  span: number,
): { readonly segs: readonly string[]; readonly inner: readonly string[] } {
  const segs: string[] = [];
  const inner: string[] = [];
  for (let i = 0; i < span; i++) {
    const l = line + i;
    segs.push(`${side}:${l}`);
    const ic = innerCellOf(rows, cols, side, l);
    inner.push(`${ic.row},${ic.col}`);
  }
  return { segs, inner };
}

/** True iff any covered segment OR inmost cell of (side,line,span) collides with already-placed gates. */
function spanCollides(
  rows: number,
  cols: number,
  side: Side,
  line: number,
  span: number,
  takenSeg: ReadonlySet<string>,
  takenInner: ReadonlySet<string>,
): boolean {
  const { segs, inner } = gateKeys(rows, cols, side, line, span);
  return segs.some((s) => takenSeg.has(s)) || inner.some((c) => takenInner.has(c));
}

interface PlacedGate {
  readonly side: Side;
  readonly line: number;
  readonly span: number;
}

/**
 * Choose a flat side + line for `shapeId` that does not overlap an already-placed gate. Consumes rng
 * (side draw, then line draws with bounded re-rolls); falls back to a deterministic scan (no rng) over
 * all placeable sides/lines so the stream stays predictable when random rolls exhaust.
 */
function chooseGateSegment(
  rows: number,
  cols: number,
  shapeId: ShapeId,
  sides: readonly Side[],
  takenSeg: ReadonlySet<string>,
  takenInner: ReadonlySet<string>,
  rng: Rng,
): PlacedGate | null {
  const side = sides[nextInt(rng, sides.length)]!;
  const span = spanOf(shapeId, side);
  const maxLine = edgeLen(rows, cols, side) - span; // inclusive upper bound for `line`
  if (maxLine >= 0) {
    let line = nextInt(rng, maxLine + 1);
    let attempts = 0;
    const maxAttempts = (maxLine + 1) * 8 + 16;
    while (spanCollides(rows, cols, side, line, span, takenSeg, takenInner) && attempts < maxAttempts) {
      line = nextInt(rng, maxLine + 1);
      attempts++;
    }
    if (!spanCollides(rows, cols, side, line, span, takenSeg, takenInner)) {
      return { side, line, span };
    }
  }
  // Deterministic fallback: scan every placeable side/line (consumes no rng).
  for (const s of sides) {
    const sp = spanOf(shapeId, s);
    const ml = edgeLen(rows, cols, s) - sp;
    for (let line = 0; line <= ml; line++) {
      if (!spanCollides(rows, cols, s, line, sp, takenSeg, takenInner)) {
        return { side: s, line, span: sp };
      }
    }
  }
  return null;
}

/** True iff `shapeId` is a 1-wide bar (bounding-box min dimension == 1): dot/duo/tri/iBar. */
function isThinShape(shapeId: ShapeId): boolean {
  const sb = shapeBounds(SHAPES[shapeId].cells);
  return Math.min(sb.rows, sb.cols) === 1;
}

/**
 * Pick one shape from `feasible`, BIASED toward BENT pieces — L/J trominoes and L/J/T tetrominoes —
 * because the reference board is full of corner/T shapes, not just straight bars. Consumes exactly
 * ONE rng draw (same budget as a uniform `pick`, so the downstream stream stays deterministic).
 * Weighting: a BEND (bounding min dim >= 2) gets weight a*a (lTri/jTri 9, tTet/lTet/jTet 16) so the
 * bends dominate every board; a thin 1-wide bar gets the LOW weight `a` (dot 1, duo 2, tri 3, 1x4
 * bar 4) so straight bars still show up as the occasional funnel piece but never take over. Fat 2x2
 * squares never reach here (excluded from every pool).
 * Determinism: a single rng() value indexes a cumulative-weight table built in `feasible` order.
 */
function pickBendBiased(rng: Rng, feasible: readonly ShapeId[]): ShapeId {
  let total = 0;
  const cum: number[] = [];
  for (const id of feasible) {
    const a = SHAPES[id].cells.length;
    const w = isThinShape(id) ? a : a * a; // bends (a*a) dominate; straight bars (a) are occasional
    total += w;
    cum.push(total);
  }
  const r = rng() * total;
  for (let i = 0; i < feasible.length; i++) {
    if (r < cum[i]!) {
      return feasible[i]!;
    }
  }
  return feasible[feasible.length - 1]!;
}

export interface GatePlan {
  readonly gates: readonly Gate[];
  /** The single shape every block of a given color uses (one shape per color per level). */
  readonly shapeByColor: ReadonlyMap<Color, ShapeId>;
}

/**
 * One gate per color, each AUTO-FIT to that color's single chosen shape (§5 gate.span): pick a shape
 * from the feasible pool, a flat exit side, then a non-overlapping line such that span ==
 * shapeLanes(shape, side).length. Distinct perimeter segments + distinct inmost cells across gates.
 * Consumes rng per color in a fixed order (shape, side, line...) so generation stays deterministic.
 *
 * SHAPE/COLOR DECOUPLING: the shape for each color is drawn INDEPENDENTLY from the same feasible pool
 * (no color→shape lookup table), so any shape can appear in any color and two colors may even share a
 * shape. The only coupling kept is the structural gate-fit invariant — within a single level a color's
 * gate span must equal that color's piece width — which is exactly what "one shape per color" buys; it
 * does NOT tie a shape *type* to a color. Color variety/balance is handled by the difficulty's
 * colorCount ramp + the spawn loop's least-used-color balancing.
 */
export function planGates(
  rows: number,
  cols: number,
  colors: readonly Color[],
  feasible: readonly ShapeId[],
  rng: Rng,
): GatePlan {
  const gates: Gate[] = [];
  const shapeByColor = new Map<Color, ShapeId>();
  const takenSeg = new Set<string>();
  const takenInner = new Set<string>();
  for (const color of colors) {
    const shapeId = pickBendBiased(rng, feasible); // draw 1: this color's shape (bend-biased)
    const sides = placeableSides(rows, cols, shapeId);
    if (sides.length === 0) {
      continue; // unreachable for the catalog on v1 boards; drop this color's gate.
    }
    const placed = chooseGateSegment(rows, cols, shapeId, sides, takenSeg, takenInner, rng);
    if (placed === null) {
      continue; // perimeter exhausted (practically never for v1 sizes); drop this color's gate.
    }
    gates.push({ color, side: placed.side, line: placed.line, span: placed.span });
    shapeByColor.set(color, shapeId);
    const { segs, inner } = gateKeys(rows, cols, placed.side, placed.line, placed.span);
    for (const s of segs) takenSeg.add(s);
    for (const c of inner) takenInner.add(c);
  }
  return { gates, shapeByColor };
}

// ---- 10.4 construction -----------------------------------------------------

interface Built {
  readonly initialState: GameState;
  readonly solution: readonly Move[];
}

/**
 * Construct one candidate level from a single rng stream already positioned at the §11.C step
 * (color selection). Returns null when blockCount cannot be reached (caller retries with seed+1).
 *
 * Spawn loop (§11.B): repeatedly spawn a block on a gate whose inmost cell is currently empty. When
 * every inmost cell is occupied, interleave a reverse-slide to retract a resting block deeper and
 * free a cell, then continue. Each reverse-slide here is recorded in application order (its forward
 * inverse is part of the solution). When neither a spawn nor a clearing slide is possible we cannot
 * reach blockCount → return null.
 */
function constructFrom(difficulty: ResolvedDifficulty, rng: Rng): Built | null {
  // §11.C — color selection: one shuffle pass, take the first colorCount.
  const colors = shuffle(rng, COLORS).slice(0, difficulty.colorCount);
  // Per-color shape + auto-fit gates (§5, Task 5): one shape per color, gate span sized to it.
  const feasible = feasibleShapes(difficulty);
  const { gates, shapeByColor } = planGates(difficulty.rows, difficulty.cols, colors, feasible, rng);
  const board: Board = createBoard(difficulty.rows, difficulty.cols, gates);

  let state: GameState = { board, blocks: [] };
  const recorded: Move[] = [];

  // §11.B — spawn loop. A gate is spawnable iff its color's shape can be placed flush right now
  // (footprint on-board + unoccupied + exit-eligible). We try every gate, then pick one to commit.
  // COLOR BALANCE: among the spawnable gates we restrict the random draw to the color(s) with the
  // FEWEST pieces placed so far, so blocks spread evenly across the level's colors instead of one
  // color running away with the board. This consumes exactly one rng draw (the same `pick`), so the
  // downstream stream stays deterministic. Because every color is auto-fit to its own gate span, the
  // gate-fit invariant (lane width == gate span per color) is untouched by this balancing.
  let spawned = 0;
  let guard = 0;
  const spawnedByColor = new Map<Color, number>();
  const guardMax = (difficulty.blockCount + difficulty.rows + difficulty.cols) * 8 + 128;
  while (spawned < difficulty.blockCount && guard < guardMax) {
    guard++;
    const options: { readonly step: ReverseStep; readonly color: Color }[] = [];
    for (const gate of gates) {
      const shapeId = shapeByColor.get(gate.color);
      if (shapeId === undefined) {
        continue;
      }
      const step = reverseExit(state, gate, spawned, shapeId);
      if (step !== null) {
        options.push({ step, color: gate.color });
      }
    }
    if (options.length > 0) {
      // Restrict to the least-used color(s) among the spawnable gates, then draw uniformly.
      const minUsed = Math.min(...options.map((o) => spawnedByColor.get(o.color) ?? 0));
      const balanced = options.filter((o) => (spawnedByColor.get(o.color) ?? 0) === minUsed);
      const chosen = pick(rng, balanced); // §11.B: draw a least-used spawnable color
      recorded.push(chosen.step.reverse.forward);
      state = chosen.step.state;
      spawnedByColor.set(chosen.color, (spawnedByColor.get(chosen.color) ?? 0) + 1);
      spawned++;
    } else {
      // No gate can spawn right now: retract a resting block to free room (interleaved reverse-slide).
      const slide = reverseSlide(state, rng);
      if (slide === null) {
        break; // cannot free room for any shape → cannot reach blockCount
      }
      recorded.push(slide.reverse.forward);
      state = slide.state;
    }
  }
  if (spawned < difficulty.blockCount) {
    return null;
  }

  // §11.S — scramble loop: each reverse-slide draws (block,dir) then distance k. Nulls are skipped.
  for (let i = 0; i < difficulty.scrambleMoves; i++) {
    const slide = reverseSlide(state, rng);
    if (slide === null) {
      continue; // no legal reverse-slide right now; skip (no infinite retry)
    }
    recorded.push(slide.reverse.forward);
    state = slide.state;
  }

  // §10.4.4 — solution is the recorded forward moves in reverse application order.
  const solution = recorded.slice().reverse();
  return { initialState: state, solution };
}

// ---- 10.5 replay-to-verify gate --------------------------------------------

export type VerifyReason = 'ok' | 'noop-move' | 'illegal-move' | 'not-cleared';

export interface VerifyResult {
  readonly solved: boolean; // true ⇔ every move applied (legal & non-noop) AND final blocks === 0
  readonly movesApplied: number;
  readonly failedAtIndex: number | null; // index of first bad move; null when solved
  readonly reason: VerifyReason;
}

/**
 * Replays `solution` forward from `initialState` via the engine (§6): every move must be legal AND
 * non-noop, and the final state must satisfy isSolved(). The §3.3 assertion gate — a failure is a
 * generator bug handled by seed+1 regeneration.
 */
export function verifySolution(
  initialState: GameState,
  solution: readonly Move[],
): VerifyResult {
  let state = initialState;
  let applied = 0;
  for (let i = 0; i < solution.length; i++) {
    const move = solution[i]!; // guarded by i < length
    if (getBlock(state, move.blockId) === undefined) {
      return { solved: false, movesApplied: applied, failedAtIndex: i, reason: 'illegal-move' };
    }
    const res = applyMove(state, move);
    if (res.result.kind === 'noop') {
      return { solved: false, movesApplied: applied, failedAtIndex: i, reason: 'noop-move' };
    }
    state = res.state;
    applied++;
  }
  if (isSolved(state)) {
    return { solved: true, movesApplied: applied, failedAtIndex: null, reason: 'ok' };
  }
  return { solved: false, movesApplied: applied, failedAtIndex: null, reason: 'not-cleared' };
}

// ---- entry points ----------------------------------------------------------

function buildVerified(
  levelIndex: number,
  baseSeed: number,
  maxRetries: number,
  // When provided, difficulty is fixed (free play); otherwise it is resolved per-seed (campaign).
  fixedDifficulty: ResolvedDifficulty | null,
): GeneratedLevel {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    const seed = baseSeed + attempt;
    const rng = mulberry32(seed);
    // §11.R — campaign levels resolve difficulty from the SAME rng (one draw: scramble jitter) before constructing.
    // Free play uses the caller's explicit difficulty and starts the stream at §11.C.
    const difficulty =
      fixedDifficulty === null ? resolveDifficulty(levelIndex, rng) : fixedDifficulty;
    const built = constructFrom(difficulty, rng);
    if (built !== null) {
      const verify = verifySolution(built.initialState, built.solution);
      if (verify.solved) {
        return {
          levelIndex,
          seed,
          difficulty,
          initialState: built.initialState,
          solution: built.solution,
          par: built.solution.length,
        };
      }
    }
    // Verify failed or blockCount unreachable → seed+1 (§11.6, M5).
  }
  throw new Error(
    `generateLevel: failed to produce a verified level for index ${levelIndex} within ${maxRetries} retries`,
  );
}

/** Deterministic, always-solvable level for the endless campaign. Runs the verify gate (§10.5). */
export function generateLevel(levelIndex: number, options?: GenerateOptions): GeneratedLevel {
  const maxRetries = options?.maxRetries ?? DEFAULT_MAX_RETRIES;
  return buildVerified(levelIndex, seedForLevel(levelIndex), maxRetries, null);
}

/** Free Play / Shuffle (§3.3): one-off board from an arbitrary seed + explicit difficulty. */
export function generateFromSeed(
  seed: number,
  difficulty: ResolvedDifficulty,
  options?: GenerateOptions,
): GeneratedLevel {
  const maxRetries = options?.maxRetries ?? DEFAULT_MAX_RETRIES;
  return buildVerified(-1, seed, maxRetries, difficulty);
}
